import { Box } from "@mui/material";
import {
  DescriptionOutlined as TemplateIcon,
  ChevronLeft as PickIcon,
  ChevronRight as BackIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "../ui/v2Tokens";
import V2Dialog from "../ui/V2Dialog";
import { EmptyState, TextButton } from "../ui/primitives";
import { useMenuTemplatesV2 } from "../data/useNutritionV2";

export type CreateMenuChoice = { templateId?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (choice: CreateMenuChoice) => void;
};

/**
 * Step one of creating a menu: start empty, or start from a company menu template.
 * The menu TYPE (detailed plan vs macro goals) is decided later — after the calculator.
 */
const CreateMenuDialog = ({ open, onClose, onCreate }: Props) => {
  const { data: templates, loading: templatesLoading } = useMenuTemplatesV2();
  const [step, setStep] = useState<"mode" | "template">("mode");

  const close = () => {
    setStep("mode");
    onClose();
  };

  return (
    <V2Dialog
      open={open}
      onClose={close}
      title="צור תפריט תזונה חדש"
      subtitle={step === "mode" ? "בחר אופן יצירת התפריט" : "בחר תבנית תפריט של החברה"}
      maxWidth="560px"
      actions={
        <>
          <TextButton onClick={close}>ביטול</TextButton>
          {step === "template" && <TextButton onClick={() => setStep("mode")}>
              <BackIcon sx={{ fontSize: 16 }} />
              חזרה
            </TextButton>}
        </>
      }
    >
      {step === "mode" ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: "12px" }}>
          <Box
            component="button"
            type="button"
            onClick={() => onCreate({})}
            sx={{
              textAlign: "start",
              padding: "18px 16px",
              borderRadius: v2.rSm,
              border: `1.5px solid ${v2.line}`,
              background: v2.surface,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all .15s",
              "&:hover": { borderColor: v2.brand, background: v2.brandWash },
            }}
          >
            <Box sx={{ fontSize: "14px", fontWeight: 700, color: v2.ink, marginBottom: "6px" }}>צור חדש</Box>
            <Box sx={{ fontSize: "12px", color: v2.ink2, lineHeight: 1.5 }}>
              התחל מתפריט ריק ובנה את כל הארוחות בעצמך
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => setStep("template")}
            sx={{
              textAlign: "start",
              padding: "18px 16px",
              borderRadius: v2.rSm,
              border: `1.5px solid ${v2.line}`,
              background: v2.surface,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all .15s",
              "&:hover": { borderColor: "#5B4BA8", background: "#F7F5FE" },
            }}
          >
            <Box sx={{ fontSize: "14px", fontWeight: 700, color: v2.ink, marginBottom: "6px" }}>
              השתמש בתבנית
            </Box>
            <Box sx={{ fontSize: "12px", color: v2.ink2, lineHeight: 1.5 }}>
              התחל מתבנית תפריט קיימת של החברה והתאם אותה למתאמן
              {templates.length > 0 && ` · ${templates.length} תבניות`}
            </Box>
          </Box>
        </Box>
      ) : templatesLoading ? (
        // "still loading" must not read as "you have none" — a trainer with templates would
        // otherwise be told to go and create one
        <EmptyState icon={TemplateIcon} text="טוען תבניות…" />
      ) : templates.length === 0 ? (
        <EmptyState
          icon={TemplateIcon}
          text='אין תבניות תפריט עדיין. שמור תפריט קיים כתבנית מרשימת התפריטים ("שמור כתבנית").'
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {templates.map((template) => (
            <Box
              key={template.id}
              component="button"
              type="button"
              onClick={() => onCreate({ templateId: template.id })}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                width: "100%",
                textAlign: "start",
                padding: "11px 14px",
                borderRadius: v2.rSm,
                border: `1.5px solid ${v2.line}`,
                background: v2.surface,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { borderColor: "#5B4BA8", background: "#F7F5FE" },
              }}
            >
              <Box>
                <Box sx={{ fontSize: "13px", fontWeight: 700, color: v2.ink }}>{template.name}</Box>
                <Box sx={{ fontSize: "11px", color: v2.ink2, marginTop: "2px" }}>
                  {template.meals.length} ארוחות
                </Box>
              </Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", fontSize: "11px", color: "#5B4BA8", fontWeight: 700, whiteSpace: "nowrap" }}>
                בחר
                <PickIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </V2Dialog>
  );
};

export default CreateMenuDialog;
